import styled, {
  css,
  FlattenSimpleInterpolation,
} from "styled-components/macro";
import { darken, lighten, rem } from "polished";
import React from "react";
import colors from "../theme/colors";
import media from "../theme/media";

type ButtonVariantType = "primary" | "secondary" | "alert";

// Utility function to create event handlers for both click and keyboard events
export const createActionHandlers = (
  action: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<Element>) => void
) => ({
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    action(e);
  },
  onKeyDown: (e: React.KeyboardEvent<Element>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action(e);
    }
  },
});

const buttonStyles: { [K in ButtonVariantType]: FlattenSimpleInterpolation } = {
  primary: css`
    background-color: ${colors.blue};
    color: white;
    &:hover {
      background-color: ${darken(0.1, colors.blue)};
    }

    &:active {
      background-color: ${lighten(0.1, colors.blue)};
    }
  `,
  secondary: css``,
  alert: css`
    background: ${colors.error};
    color: white;
  `,
};

const StyledButton = styled.button<{
  auto?: boolean;
  variant?: ButtonVariantType;
  small?: boolean;
}>`
  border: none;
  outline: none;
  border-radius: 3px;
  min-width: ${rem(161)};
  min-height: ${rem(48)};
  font-size: ${rem(16)};
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  padding: 0 1em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  /* Focus styles for keyboard navigation */
  &:focus-visible {
    outline: 2px solid ${colors.blue};
    outline-offset: 2px;
  }
  
  /* Remove focus outline for mouse users */
  &:focus:not(:focus-visible) {
    outline: none;
  }
  
  ${({ auto }) =>
    auto &&
    `
    min-width: unset;
    width: auto;
  `};

  &:hover:disabled,
  &:disabled {
    background: #b3b3b3;
    cursor: not-allowed;
    pointer-events: none;
  }
  ${({ variant }) => (variant ? buttonStyles[variant] : buttonStyles.primary)}

  ${({ small }) => css`
    min-width: ${rem(120)};
    min-height: ${rem(36)};
    font-size: ${rem(14)};
  `}

  ${media.lessThan("tablet")`
    min-width: 5rem;
  `}
`;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  auto?: boolean;
  variant?: ButtonVariantType;
  small?: boolean;
  action?: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<Element>) => void;
}

const Button: React.FC<ButtonProps> = ({ action, ...props }) => {
  if (action) {
    const handlers = createActionHandlers(action);
    return <StyledButton {...handlers} {...props} />;
  }
  
  return <StyledButton {...props} />;
};

export default Button;
